import { apiClient } from "./api";

export interface CreditSummary {
  available_credits: number;
  total_credits_used: number;
  daily_average_usage: number;
  last_transaction_date: string;
  current_period_usage: number;
  lifetime_granted_credits: number;
}

export interface DailyUsage {
  date: string;
  credits_used: number;
  api_calls: number;
  amount?: number; // Added for chart compatibility
}

export interface CreditHistory {
  user_id: string;
  daily_usage: DailyUsage[];
  total_credits_used: number;
  total_api_calls: number;
  date_range: {
    start: string;
    end: string;
  };
}

export interface CreditTransaction {
  transaction_id: string;
  timestamp: string;
  amount: number;
  transaction_type:
    | "purchase"
    | "usage"
    | "system_grant"
    | "refund"
    | "promo_grant"
    | "consumption";
  metadata: Record<string, any>;
}

export interface ProductUsageData {
  credits: number;
  total_credits: number;
  calls?: number;
  total_calls: number;
  unique_agents: number;
  daily_usage: Array<{
    date: string;
    credits: number;
    calls: number;
  }>;
  average_credits_per_call: number;
}

// API Functions
export const getUserCreditSummary = async (
  userId: string,
): Promise<CreditSummary> => {
  const response = await apiClient.get(`/api/credits/summary/${userId}`);
  return response.data;
};

export const getUserCreditHistory = async (
  userId: string,
  start_date?: string,
  end_date?: string,
): Promise<CreditHistory> => {
  const response = await apiClient.get(`/api/credits/history/${userId}`, {
    params: { start_date, end_date },
  });
  return response.data;
};

export const getUserProductUsage = async (
  userId: string,
  start_date?: string,
  end_date?: string,
): Promise<Record<string, ProductUsageData>> => {
  const response = await apiClient.get(
    `/api/credits/usage/by-product/${userId}`,
    {
      params: { start_date, end_date },
    },
  );
  return response.data;
};

export const getUserTransactions = async (
  userId: string,
  page: number = 1,
  limit: number = 10,
): Promise<{ transactions: CreditTransaction[]; total: number }> => {
  const response = await apiClient.get(`/api/credits/transactions/${userId}`, {
    params: { page, limit },
  });
  return response.data;
};

// React Query Hooks
export const userCreditQueries = {
  all: (userId: string) => ({ queryKey: ["user-credits", userId, "summary"] }),
  summary: (userId: string) => ({
    queryKey: ["user-credits", userId, "summary"],
    queryFn: () => getUserCreditSummary(userId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  }),

  history: (userId: string, startDate?: string, endDate?: string) => ({
    queryKey: ["user-credits", userId, "history", startDate, endDate],
    queryFn: () => getUserCreditHistory(userId, startDate, endDate),
    staleTime: 1000 * 60 * 5,
  }),

  productUsage: (userId: string, startDate?: string, endDate?: string) => ({
    queryKey: ["user-credits", userId, "product-usage", startDate, endDate],
    queryFn: () => getUserProductUsage(userId, startDate, endDate),
    staleTime: 1000 * 60 * 5,
  }),

  transactions: (userId: string, page: number = 1, limit: number = 10) => ({
    queryKey: ["user-credits", userId, "transactions", page, limit],
    queryFn: () => getUserTransactions(userId, page, limit),
    staleTime: 1000 * 60 * 5,
  }),
};
